/**
 * The customer list, with its inline editor and the archive view.
 *
 * The port of `Views/CustomersPage.xaml` + `CustomersViewModel`. Two rules from that page carry over
 * unchanged and are the reason it looks the way it does:
 *
 *   * RETIRING A CUSTOMER IS NOT DONE FROM HERE. A row shows a name and a device count, which is not
 *     enough to decide on; the customer's own page shows the devices, people, open tickets and
 *     invoices that are about to be stood down, so Archive and the permanent deletion that follows
 *     it both live there. This page keeps Restore, because putting one back is the archive view's
 *     whole purpose.
 *   * THE ORPHANED INVOICES ARE ONLY ON THE ARCHIVE VIEW. They are the residue of a permanent
 *     deletion - issued and paid invoices kept because they record money actually billed - and this
 *     is the only place in the CRM that lists them, so leaving them off would make them unreachable.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import type { CustomerDto, InvoiceDto } from '../contracts.gen.js';
import { money } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  hstack,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import { moneyBox } from '../ui/MoneyBox.js';
import type { CrmPage, PageContext } from './page.js';

export function customersPage(): CrmPage {
  let archived = false;
  /** 0 for a new customer; the id being edited otherwise. Mirrors `_editId` on the view model. */
  let editingId = 0;

  return {
    mount(host: Container, ctx: PageContext) {
      const status = new StatusLine();

      // ---- header ----
      const title = h1('Customers');
      const subtitle = muted('');
      const newButton = button('New customer', () => beginNew(), true);
      const backButton = button('← Back to customers', () => void switchView(false));
      backButton.visible = false;

      const header = splitRow({ align: 'start' });
      fillWith(header, stackOf(vstack({ spacing: GAP.xs }), title, subtitle, status.label));
      header.controls.add(newButton);
      header.controls.add(backButton);

      // ---- editor ----
      const editorTitle = h2('New customer');
      const nameBox = textBox();
      const contactBox = textBox();
      const emailBox = textBox();
      const phoneBox = textBox();
      const addressBox = textBox();
      const termsBox = textBox({ text: 'Net 30' });
      const rate = moneyBox();

      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Name', nameBox), field('Contact name', contactBox)),
          fieldRow(field('Email', emailBox), field('Phone', phoneBox)),
          field('Address', addressBox),
          fieldRow(field('Billing terms', termsBox), field('Default rate per hour', rate.row)),
          stackOf(
            buttonRow(),
            button('Save', () => void save(), true),
            button('Cancel', () => closeEditor())
          )
        )
      );

      // ---- list ----
      const list = new CardList<CustomerDto>({
        key: c => c.id,
        build: c => buildCustomerCard(c)
      });
      const empty = new EmptyState('No customers yet.');

      // ---- orphaned invoices ----
      const orphanList = new CardList<InvoiceDto>({
        key: i => i.id,
        build: i => buildOrphanCard(i)
      });
      const orphans = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        h2('Invoices from deleted customers'),
        muted(
          'Issued and paid invoices are kept when a customer is deleted permanently. Open one to read or print it.'
        ),
        orphanList.panel
      );

      const archiveLink = link('View archived customers →', () => void switchView(true));

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack(), header, editor, list.panel, empty.panel, orphans, archiveLink)
      );
      host.controls.add(view);

      // ---------------------------------------------------------------
      //  Cards
      // ---------------------------------------------------------------

      function buildCustomerCard(c: CustomerDto): Card {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          text(c.name, { font: FONT.h2 }),
          muted(c.contactName || 'No contact named')
        );
        if (c.archived) left.controls.add(chip('Archived', 'warn'));

        const count = stackOf(
          vstack({ spacing: 0, autoWidth: true, hAlign: 'center', align: 'center' }),
          value(String(c.deviceCount), { font: FONT.bold, role: 'brand' }),
          value('devices', { font: FONT.tiny, role: 'muted' })
        );

        const actions = hstack({ spacing: GAP.sm });
        actions.controls.add(count);
        actions.controls.add(button('Open', () => ctx.go(`/customers/${c.id}`), true));
        if (!archived) actions.controls.add(button('Edit', () => beginEdit(c)));
        else actions.controls.add(button('Restore', () => void restore(c)));

        const row = splitRow();
        fillWith(row, left);
        row.controls.add(actions);

        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function buildOrphanCard(invoice: InvoiceDto): Card {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          text(invoice.number, { font: FONT.bold }),
          muted(invoice.customerName)
        );
        const figures = stackOf(
          vstack({ spacing: 0, autoWidth: true, align: 'end' }),
          value(invoice.statusLabel, { font: FONT.tiny, role: 'muted' }),
          value(money(invoice.total), { font: FONT.bold })
        );
        const actions = hstack({ spacing: GAP.sm });
        actions.controls.add(figures);
        actions.controls.add(button('Open', () => ctx.go(`/invoices/${invoice.id}`), true));

        const row = splitRow();
        fillWith(row, left);
        row.controls.add(actions);

        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      // ---------------------------------------------------------------
      //  Editor
      // ---------------------------------------------------------------

      function beginNew(): void {
        editingId = 0;
        editorTitle.text = 'New customer';
        nameBox.text = '';
        contactBox.text = '';
        emailBox.text = '';
        phoneBox.text = '';
        addressBox.text = '';
        termsBox.text = 'Net 30';
        rate.set(0);
        openEditor();
      }

      function beginEdit(c: CustomerDto): void {
        editingId = c.id;
        editorTitle.text = 'Edit customer';
        nameBox.text = c.name ?? '';
        contactBox.text = c.contactName ?? '';
        emailBox.text = c.contactEmail ?? '';
        phoneBox.text = c.contactPhone ?? '';
        addressBox.text = c.address ?? '';
        termsBox.text = c.billingTerms ?? '';
        rate.set(c.defaultRate);
        openEditor();
      }

      function openEditor(): void {
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editor.visible = false;
        relayout(content);
      }

      async function save(): Promise<void> {
        const name = nameBox.text.trim();
        if (!name) {
          status.error('Name is required.');
          return;
        }
        const body = {
          name,
          contactName: contactBox.text.trim(),
          contactEmail: emailBox.text.trim(),
          contactPhone: phoneBox.text.trim(),
          address: addressBox.text.trim(),
          billingTerms: termsBox.text.trim(),
          defaultRate: rate.value() ?? 0
        };
        status.busy(editingId === 0 ? 'Adding…' : 'Saving…');
        try {
          if (editingId === 0) await crm.createCustomer(body);
          else await crm.updateCustomer(editingId, body);
          if (ctx.cancelled()) return;
          status.ok(editingId === 0 ? 'Customer added.' : 'Customer updated.');
          closeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function restore(c: CustomerDto): Promise<void> {
        status.busy(`Restoring ${c.name}…`);
        try {
          await crm.restoreCustomer(c.id);
          if (ctx.cancelled()) return;
          status.ok(`Restored ${c.name} to the active list.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      // ---------------------------------------------------------------
      //  Loading
      // ---------------------------------------------------------------

      async function switchView(showArchived: boolean): Promise<void> {
        archived = showArchived;
        closeEditor();
        status.clear();
        applyChrome();
        await refresh();
      }

      function applyChrome(): void {
        title.text = archived ? 'Archived customers' : 'Customers';
        subtitle.text = archived
          ? 'Retained in full - their work, charges and invoices are all still here. Restore one here, or open it to delete it permanently.'
          : 'Open a customer to archive it; nothing is destroyed until an archived customer is deleted permanently from its own page.';
        newButton.visible = !archived;
        backButton.visible = archived;
        archiveLink.visible = !archived;
        empty.message = archived ? 'No archived customers.' : 'No customers yet.';
      }

      async function refresh(): Promise<void> {
        try {
          const customers = archived
            ? await crm.getArchivedCustomers(ctx.dispose.signal())
            : await crm.getCustomers(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          list.set(customers);
          empty.update(customers.length);

          // Only fetched for the archive view: an orphan is the residue of a permanent deletion, and
          // the active list has nothing to say about them.
          if (archived) {
            const orphaned = await crm.getOrphanedInvoices(ctx.dispose.signal());
            if (ctx.cancelled()) return;
            orphanList.set(orphaned);
            orphans.visible = orphaned.length > 0;
          } else {
            orphanList.clear();
            orphans.visible = false;
          }
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      applyChrome();
      void refresh();
    }
  };
}

